import {
  CtaBuilderFn,
  InstantAnswerBuilderFn,
  InstantAnswerScenario,
} from "@faire/help-center-self-serve/types";
import { StrictLocalizeFunction } from "@faire/web--source/common/localization";
import { intlDate } from "@faire/web--source/common/localization/date-fns/intlDate";
import { ISelfServeFaireDirectDiscount } from "@faire/web-api--source/indigofair/helpcenter/ISelfServeFaireDirectDiscount";
import { differenceInDays } from "date-fns/differenceInDays";

import { additionalDataRequiredForFaireDiscountsSignUpOfferIA } from "../../shared/instantAnswerUtils";

import { ViewOfferDetailsCTA } from "./ctas";

export const WhenDoesSignupExpireIA: InstantAnswerBuilderFn = (
  strictLocalize
) => {
  return {
    titleBuilder: (context) => {
      return getAnswer(
        strictLocalize,
        context.faireDirectDiscountsContext?.discounts
      ).getTitle();
    },

    descriptionBuilder: (context) => {
      return getAnswer(
        strictLocalize,
        context.faireDirectDiscountsContext?.discounts
      ).getDescription();
    },

    primaryCtaBuilder: {
      textBuilder: (context) => {
        return getPrimaryCTA(context, strictLocalize)?.label;
      },
      linkBuilder: (context) => {
        return getPrimaryCTA(context, strictLocalize)?.getLink?.();
      },
    },

    additionalRequiredData:
      additionalDataRequiredForFaireDiscountsSignUpOfferIA(),
  };
};

const getAnswer = (
  strictLocalize: StrictLocalizeFunction,
  discounts: ISelfServeFaireDirectDiscount[] | undefined
): InstantAnswerScenario => {
  if (discounts && discounts[0]) {
    if (discounts.length > 1) {
      return GENERAL_FAIRE_DIRECT_ANSWER(strictLocalize);
    } else if (discounts[0].is_expired) {
      return RELATIONSHIP_EXPIRED_ANSWER(strictLocalize, discounts[0]);
    } else {
      return RELATIONSHIP_NOT_EXPIRED_ANSWER(strictLocalize, discounts[0]);
    }
  } else {
    return NO_RELATIONSHIPS_ANSWER(strictLocalize);
  }
};

const getPrimaryCTA: CtaBuilderFn = (context, strictLocalize) => {
  const discounts = context.faireDirectDiscountsContext?.discounts;

  if (discounts?.length === 1 && discounts[0]) {
    return ViewOfferDetailsCTA(strictLocalize);
  }

  return undefined;
};

const GENERAL_FAIRE_DIRECT_ANSWER = (
  strictLocalize: StrictLocalizeFunction
): InstantAnswerScenario => ({
  getTitle: () => GENERAL_FAIRE_DIRECT_TITLE(strictLocalize),
  getDescription: () => [GENERAL_FAIRE_DIRECT_DESCRIPTION(strictLocalize)],
});

const GENERAL_FAIRE_DIRECT_TITLE = (strictLocalize: StrictLocalizeFunction) =>
  strictLocalize({
    defaultMessage: "How does the signup offer work",
  });

const GENERAL_FAIRE_DIRECT_DESCRIPTION = (
  strictLocalize: StrictLocalizeFunction
) =>
  strictLocalize({
    defaultMessage:
      "When you join Faire using a brand’s link, you’ll receive a signup offer, including a promotion and one year of free shipping with that brand. The offer is automatically applied at checkout and expires 7 days after signing up.",
  });

const RELATIONSHIP_EXPIRED_ANSWER = (
  strictLocalize: StrictLocalizeFunction,
  relationship: ISelfServeFaireDirectDiscount
): InstantAnswerScenario => ({
  getTitle: () => RELATIONSHIP_EXPIRED_TITLE(strictLocalize, relationship),
  getDescription: () => [
    RELATIONSHIP_EXPIRED_DESCRIPTION(strictLocalize, relationship),
  ],
});

const RELATIONSHIP_EXPIRED_TITLE = (
  strictLocalize: StrictLocalizeFunction,
  relationship: ISelfServeFaireDirectDiscount
) =>
  strictLocalize(
    {
      defaultMessage: "Your signup offer expired on {expiryDate}",
    },
    {
      expiryDate: intlDate(relationship.expires_at!, "PP"),
    }
  );
const RELATIONSHIP_EXPIRED_DESCRIPTION = (
  strictLocalize: StrictLocalizeFunction,
  relationship: ISelfServeFaireDirectDiscount
) =>
  strictLocalize(
    {
      defaultMessage:
        "Looks like you didn’t place an order with {brandCompanyName} before your signup offer expired.",
    },
    {
      brandCompanyName: relationship.brand_name!,
    }
  );

const RELATIONSHIP_NOT_EXPIRED_ANSWER = (
  strictLocalize: StrictLocalizeFunction,
  relationship: ISelfServeFaireDirectDiscount
): InstantAnswerScenario => ({
  getTitle: () =>
    differenceInDays(relationship.expires_at!, Date.now()) === 1
      ? RELATIONSHIP_NOT_EXPIRED_TITLE_SINGLE_DAY(strictLocalize)
      : RELATIONSHIP_NOT_EXPIRED_TITLE(strictLocalize, relationship),
  getDescription: () => [
    RELATIONSHIP_NOT_EXPIRED_DESCRIPTION(strictLocalize, relationship),
  ],
});

const RELATIONSHIP_NOT_EXPIRED_TITLE = (
  strictLocalize: StrictLocalizeFunction,
  relationship: ISelfServeFaireDirectDiscount
) =>
  strictLocalize(
    {
      defaultMessage:
        "You have {daysLeftNumber} days left to use your signup offer",
    },
    {
      daysLeftNumber: differenceInDays(relationship.expires_at!, Date.now()),
    }
  );
const RELATIONSHIP_NOT_EXPIRED_TITLE_SINGLE_DAY = (
  strictLocalize: StrictLocalizeFunction
) =>
  strictLocalize({
    defaultMessage: "You have 1 day left to use your signup offer",
  });
const RELATIONSHIP_NOT_EXPIRED_DESCRIPTION = (
  strictLocalize: StrictLocalizeFunction,
  relationship: ISelfServeFaireDirectDiscount
) =>
  strictLocalize(
    {
      defaultMessage:
        "When you place your first order with {brandCompanyName}, the signup offer will automatically be applied at checkout. Note that the offer expires on {expiryDate}.",
    },
    {
      brandCompanyName: relationship.brand_name!,
      expiryDate: intlDate(relationship.expires_at!, "PP"),
    }
  );

const NO_RELATIONSHIPS_ANSWER = (
  strictLocalize: StrictLocalizeFunction
): InstantAnswerScenario => ({
  getTitle: () => NO_RELATIONSHIPS_TITLE(strictLocalize),
  getDescription: () => [NO_RELATIONSHIPS_DESCRIPTION(strictLocalize)],
});

const NO_RELATIONSHIPS_TITLE = (strictLocalize: StrictLocalizeFunction) =>
  strictLocalize({
    defaultMessage: "You don’t qualify for the Faire Direct offer",
  });

const NO_RELATIONSHIPS_DESCRIPTION = (strictLocalize: StrictLocalizeFunction) =>
  strictLocalize({
    defaultMessage:
      "The offer is only available to retailers who signed up for a Faire account using a brand’s link.",
  });
